import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Download, FlaskConical, Flame, ShieldCheck, Layers, Phone } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, MaskedLines, Eyebrow } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import { useState } from "react";
import QuickView from "../components/QuickView";
import { PRODUCTS, PRODUCT_BY_SLUG, SPECIES, IMG, SITE } from "../data/products";

const VALUE_PROPS = [
  {
    icon: FlaskConical,
    title: "Palatability science",
    body: "Sweeteners and palatants engineered around the gustatory nerves — immediate sweetness burst, persistent sweetness, no aftertaste.",
  },
  {
    icon: Flame,
    title: "Pelleting stability",
    body: "High-temperature resistant granulation keeps aroma and sweetness intact from mixer to finished pellet.",
  },
  {
    icon: ShieldCheck,
    title: "Toxin management",
    body: "Multi-component binding plus enzymatic Aflatoxin B1 detoxification, built to help control M1 in milk.",
  },
  {
    icon: Layers,
    title: "Species-specific programmes",
    body: "Calf & ruminant, poultry, aqua and swine — inclusion rates and delivery tuned to each application.",
  },
];

const FEATURED = ["palatisure-650", "feedlure-301", "tox-shield-pro"];

const Home = () => {
  const heroRef = useRef(null);
  const [quick, setQuick] = useState(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const chipY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <div data-testid="page-home">
      <Seo
        title="Feed Palatants, Sweeteners & Feed Additives"
        description="AURIK INGREVIA (P) Ltd. manufactures high-intensity sweeteners, palatants, toxin binders and feed additives for calf, ruminant, poultry, aqua and swine nutrition."
      />

      {/* ---------- Hero ---------- */}
      <section ref={heroRef} data-testid="home-hero" className="grain mesh-glow relative flex min-h-screen items-center overflow-hidden">
        <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-14 px-5 pb-20 pt-32 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:pb-28 lg:pt-24">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
              <Eyebrow dark>AURIK INGREVIA (P) LTD.</Eyebrow>
            </motion.div>

            <MaskedLines
              as="h1"
              delay={0.25}
              className="mt-7 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl"
              lines={[
                <span key="a">Nature's <span className="bg-gradient-to-r from-teal-300 to-cyan-400 bg-clip-text text-transparent">palatability,</span></span>,
                <span key="b">engineered to precision.</span>,
              ]}
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-emerald-100/70 sm:text-lg"
            >
              High-intensity sweeteners, palatants and bio-scientific feed additives —
              formulated for intake, stable through pelleting, and tuned to the species
              you feed.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-request-sample"
                className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-night transition-colors hover:bg-amber-400"
              >
                Request sample / quote
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/products"
                data-testid="hero-cta-explore"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:border-teal-400 hover:text-teal-300"
              >
                Explore products
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-10 flex flex-wrap gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-200/70"
            >
              {["650x sugar sweetness", "Pellet-stable granulation", "Micropearl process"].map((chip) => (
                <span key={chip} className="glass-card rounded-full px-4 py-2">{chip}</span>
              ))}
            </motion.div>
          </div>

          <motion.div style={{ y: imgY }} className="relative hidden lg:block" data-testid="hero-visual">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="clip-frame relative overflow-hidden"
            >
              <img src={IMG.hero} alt="Macro photograph of crystalline feed sweetener granules" className="h-[540px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-night/40 via-transparent to-teal-500/10" />
            </motion.div>

            <motion.div
              style={{ y: chipY }}
              className="glass-card absolute -left-10 bottom-10 rounded-xl px-5 py-4"
              data-testid="hero-spec-chip"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">Flagship sweetener</p>
              <p className="mt-1 font-display text-xl font-extrabold text-white">PalatiSure 650</p>
              <p className="mt-1 font-mono text-[11px] text-emerald-100/70">100–200 g/ton finished feed</p>
            </motion.div>

            <div className="glass-card absolute -right-4 top-10 rounded-xl px-4 py-3" data-testid="hero-form-chip">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-300">Form</p>
              <p className="mt-1 font-mono text-[11px] font-semibold text-white">Free flow powder</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- Marquee ---------- */}
      <Marquee
        items={[
          "Palatants & Sweeteners", "Toxin Management", "Gut Health & Acidifiers",
          "Emulsifiers & Energy", "Herbal & Nutraceutical Extracts", "Mineral Nutrition",
        ]}
      />

      {/* ---------- Value props ---------- */}
      <section data-testid="home-value-props" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <Reveal>
          <Eyebrow>Why Aurik</Eyebrow>
          <div className="mt-6 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <h2 className="max-w-xl font-display text-3xl font-extrabold tracking-tight text-night sm:text-4xl">
              Four disciplines. One outcome — animals eat.
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-slate-600">
              Every Aurik product exists for a measurable reason: intake during stress,
              stability in the feed mill, toxin safety in the tank, and nutrition matched
              to the species.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUE_PROPS.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08} className="h-full">
              <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-colors duration-300 hover:border-teal-600/40">
                <span className="font-mono text-[11px] font-semibold text-teal-700">0{i + 1}</span>
                <v.icon className="mt-6 h-7 w-7 text-emerald-800" strokeWidth={1.6} />
                <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-night">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- Featured products ---------- */}
      <section data-testid="home-featured" className="border-y border-slate-200 bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Featured products</Eyebrow>
            <div className="mt-6 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
              <h2 className="max-w-lg font-display text-3xl font-extrabold tracking-tight text-night sm:text-4xl">
                Spotlight on the range
              </h2>
              <Link
                to="/products"
                data-testid="home-featured-view-all"
                className="group inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-700"
              >
                Full portfolio
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {FEATURED.map((slug, i) => (
              <ProductCard key={slug} product={PRODUCT_BY_SLUG[slug]} index={i} onQuickView={setQuick} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Species quick select ---------- */}
      <section data-testid="home-species" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <Reveal>
          <Eyebrow>Applications</Eyebrow>
          <h2 className="mt-6 max-w-xl font-display text-3xl font-extrabold tracking-tight text-night sm:text-4xl">
            Programmes per species
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SPECIES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08} className="h-full">
              <Link
                to="/applications"
                data-testid={`home-species-tile-${s.slug}`}
                className="group relative block h-72 overflow-hidden rounded-2xl bg-night"
              >
                <img src={s.image} alt={`${s.label} feed programme`} loading="lazy" className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-t from-night via-night/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">0{i + 1}</p>
                    <h3 className="mt-1 font-display text-xl font-extrabold text-white">{s.label}</h3>
                  </div>
                  <span className="rounded-full border border-white/25 p-2.5 text-white transition-colors group-hover:border-teal-400 group-hover:text-teal-300">
                    <ArrowRight className="h-4 w-4 -rotate-45 transition-transform group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section data-testid="home-cta" className="grain relative overflow-hidden bg-emerald-950">
        <div className="absolute inset-0 opacity-20">
          <img src={IMG.hero} alt="" aria-hidden="true" className="h-full w-full object-cover" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-28">
          <Reveal>
            <h2 className="max-w-2xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Put feed science in every bite.
            </h2>
            <p className="mt-5 max-w-xl text-emerald-100/70">
              Request a sample, ask for a quote, or download the technical sheet for any
              product in the Aurik range.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-request"
                className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-night transition-colors hover:bg-amber-400"
              >
                Request sample / quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/products"
                data-testid="home-cta-portfolio"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:border-teal-400 hover:text-teal-300"
              >
                <Download className="h-4 w-4" /> Product portfolio
              </Link>
              <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} data-testid="home-cta-phone" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-200/80 hover:text-white">
                <Phone className="h-4 w-4" /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </div>
  );
};

export default Home;
